// « Nouveautés » panel on the first sign-in after a version (lot S5 of the SemVer versions — see `.claude/docs` / DEPLOY.md). The text is
// generated at publication (`scripts/report-deployment.py whats-new`) and served as the static file `/whats-new.json`; this module only
// holds the pure rules: which versions to show, and how.

const RELEASE = /^(\d{1,3})\.(\d+)\.(\d+)$/;
const LEVELS = ["patch", "minor", "major"];

// `X.Y.Z` → [X, Y, Z]; null for « dev », a calendar number or any other text (the panel then stays silent).
export function parseRelease(version) {
  const match = RELEASE.exec(version || "");
  return match ? [Number(match[1]), Number(match[2]), Number(match[3])] : null;
}

export function compareReleases(a, b) {
  for (let i = 0; i < 3; i += 1) {
    if (a[i] !== b[i]) return a[i] < b[i] ? -1 : 1;
  }
  return 0;
}

const isTextList = (value) => Array.isArray(value) && value.every((item) => typeof item === "string" && item.trim() !== "");

// What the server answered, kept only when it has the expected shape; a malformed version is skipped, never an error.
export function readWhatsNew(body) {
  if (!body || !Array.isArray(body.versions)) return null;
  const versions = [];
  for (const item of body.versions) {
    if (!item || typeof item.version !== "string" || !parseRelease(item.version)) continue;
    if (!LEVELS.includes(item.level)) continue;
    const features = isTextList(item.features) ? item.features : [];
    const fixes = isTextList(item.fixes) ? item.fixes : [];
    if (features.length === 0 && fixes.length === 0) continue;
    versions.push({ version: item.version, level: item.level, date: typeof item.date === "string" ? item.date : "", features, fixes });
  }
  return { current: typeof body.current === "string" ? body.current : null, versions };
}

const newestFirst = (a, b) => compareReleases(parseRelease(b.version), parseRelease(a.version));

// The versions to show on a sign-in: newer than the last one seen and not newer than the one running. The panel only opens when one of
// them is a feature (minor) or breaking (major) release: a plain fix disturbs nobody. Newest first.
export function versionsToShow(data, lastSeen, current) {
  const seen = parseRelease(lastSeen);
  const now = parseRelease(current);
  if (!seen || !now) return [];
  const missed = data.versions
    .filter((entry) => {
      const key = parseRelease(entry.version);
      return key !== null && compareReleases(key, seen) > 0 && compareReleases(key, now) <= 0;
    })
    .sort(newestFirst);
  return missed.some((entry) => entry.level !== "patch") ? missed : [];
}

// The latest versions (up to `count`) contained in the running one: to reopen the panel by hand.
export function recentVersions(data, current, count = 3) {
  const now = parseRelease(current);
  if (!now) return [];
  return data.versions.filter((entry) => compareReleases(parseRelease(entry.version), now) <= 0).sort(newestFirst).slice(0, count);
}

export const isMajorUpdate = (versions) => versions.some((entry) => entry.level === "major");

export function whatsNewTitle(versions) {
  if (versions.length === 0) return "Nouveautés";
  return isMajorUpdate(versions) ? "Grande mise à jour de Jamora CRM" : "Nouveautés de Jamora CRM";
}

export function formatReleaseDate(date) {
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(date || "") ? new Date(`${date}T00:00:00`) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "";
}
